/**
 * Hyperlocal landing page — runtime.
 *
 * Scope is deliberately small:
 *   1. Derive the ambient hue from the local time of day (warm family only).
 *   2. Rotate the subtitle line through a curated pool (~7s crossfade).
 *   3. Animate the starfield behind the hero (canvas 2D).
 *   4. Subtle mouse-parallax on the globe (desktop only).
 *   5. Track mouse position inside city doors for the radial hover glow.
 *   6. Mount the top-right auth pill — guests stay anonymous unless they
 *      actively click "Sign in". Pill switches to the user's display name
 *      once the session cookie resolves to an account.
 *
 * No external deps beyond the self-hosted display font. Everything is
 * ~6 KB gzipped (+ ~6 KB once the user actually opens the auth modal —
 * the modal is dynamically imported via the auth module's lazy paths to
 * keep the LCP cost minimal).
 */

// Self-hosted Space Grotesk 700 (woff2 via Vite) — the display stacks in
// index.html declare it, so it must actually load here or they'd be a lie.
import "@fontsource/space-grotesk/700.css";

import { mountAuthPill } from "./auth";
import { DUR } from "../ui/tokens";

/* -------------------------------------------------------------------------- */
/*  1. Ambient hue — the local time of day picks it                           */
/* -------------------------------------------------------------------------- */

// Time is the protagonist: the background carries the light of the visitor's
// hour — dawn 34, day 44, dusk 28 (ember), night 38 (lamp glow). Warm family
// only; the old random pick landed on cold teals ~37% of visits, which read
// as a different brand. Reading the clock to choose ambience is fine — the
// honesty rule bans wall-clock *copy*, not wall-clock light.
function hueForHour(hour: number): number {
  if (hour >= 5 && hour < 9) return 34;   // dawn
  if (hour >= 9 && hour < 17) return 44;  // day
  if (hour >= 17 && hour < 20) return 28; // dusk
  return 38;                              // night
}

function seedHue(): void {
  const hue = hueForHour(new Date().getHours());
  document.documentElement.style.setProperty("--hue", String(hue));
}

/* -------------------------------------------------------------------------- */
/*  2. Rotating subtitle                                                      */
/* -------------------------------------------------------------------------- */

// Rotating subtitles. The headline is now the vision itself - "Understand
// any place in 30 seconds." - so the subtitle rotation carries the brand
// voice lines that used to lead, plus the emotional promise.
const SUBTITLES = [
  "Ground reality. Mapped.",
  "Opinions ask. Maps answer.",
  "See what is happening - and what it means.",
  "Verified over viral.",
  "Built for decisions.",
  "What's real. Not what's trending.",
  "Less noise. More signal.",
  "Local truth, on a map.",
  "Signal beats sentiment.",
];

// ~7s per line: reading pace, not a ticker. The crossfade leans on the .sub
// opacity transition in index.html (var(--dur-reveal)) — the swap waits the
// same DUR.reveal so out-fade and in-fade are symmetric.
const SUBTITLE_ROTATE_MS = 7000;

function initSubtitleRotation(): void {
  const el = document.getElementById("rotating-sub");
  if (!el) return;
  el.textContent = SUBTITLES[0];
  // Reduced-motion users keep the static first line — the rotation is
  // ambience, not content; every line carries the same promise.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  let idx = 0;
  window.setInterval(() => {
    // Hidden tabs don't churn text — no point fading for nobody, and the
    // line on return should be the one the user left.
    if (document.hidden) return;
    idx = (idx + 1) % SUBTITLES.length;
    el.style.opacity = "0";
    window.setTimeout(() => {
      el.textContent = SUBTITLES[idx];
      el.style.opacity = "1";
    }, DUR.reveal);
  }, SUBTITLE_ROTATE_MS);
}

/* -------------------------------------------------------------------------- */
/*  3. Starfield canvas                                                       */
/*                                                                            */
/*  Three layers of parallax stars + tiny drifting satellites with blinking   */
/*  lights + occasional shooting stars. All on a single 2D canvas to keep     */
/*  cost at one rAF loop. Density and satellite count scale with viewport so  */
/*  mobile stays cheap (≈80 stars, 2 satellites) while desktop fills (≈220    */
/*  stars, 4 satellites). Reduced-motion users get a single static frame.     */
/* -------------------------------------------------------------------------- */

interface Star {
  x: number;
  y: number;
  z: number;       // 0.2 .. 1 — depth
  r: number;       // radius
  tw: number;      // twinkle phase
  speed: number;   // per-star twinkle-rate multiplier (0.6 .. 2.4)
  flasher: boolean; // ~14% of stars — sharper, brighter pulse
  warm: boolean;   // ~18% warm-tinted to match brand gold
  vy: number;      // gentle vertical drift component
}

interface Satellite {
  x: number;
  y: number;
  vx: number;       // px / sec
  vy: number;
  body: number;     // body half-size
  panel: number;    // solar-panel half-length along its long axis
  blinkPhase: number;
  blinkBlue: boolean; // true = blue beacon, false = red
  rot: number;      // rotation = direction of travel
}

interface Shooting {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number; // 1 → 0
}

function initStarfield(): void {
  const canvas = document.getElementById("stars") as HTMLCanvasElement | null;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isMobile = window.matchMedia("(max-width: 640px)").matches;

  let stars: Star[] = [];
  let satellites: Satellite[] = [];
  let shooting: Shooting | null = null;
  let nextShootingAt = 0;
  let w = 0;
  let h = 0;
  let last = 0;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  function makeSatellite(fromEdge: boolean): Satellite {
    let x = 0;
    let y = 0;
    if (fromEdge) {
      // Spawn just off one of the four edges, aimed across the screen.
      const edge = Math.floor(Math.random() * 4);
      if (edge === 0) { x = Math.random() * w; y = -12; }
      else if (edge === 1) { x = w + 12; y = Math.random() * h; }
      else if (edge === 2) { x = Math.random() * w; y = h + 12; }
      else { x = -12; y = Math.random() * h; }
    } else {
      x = Math.random() * w;
      y = Math.random() * h;
    }
    // Aim roughly toward the opposite half of the canvas so it actually
    // crosses the visible area instead of clipping along an edge.
    const targetX = w - x + (Math.random() - 0.5) * w * 0.4;
    const targetY = h - y + (Math.random() - 0.5) * h * 0.4;
    const angle = Math.atan2(targetY - y, targetX - x) + (Math.random() - 0.5) * 0.4;
    const speed = 18 + Math.random() * 26; // px/sec — gentle drift
    return {
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      body: 1.0 + Math.random() * 0.7,
      panel: 3.0 + Math.random() * 2.0,
      blinkPhase: Math.random() * Math.PI * 2,
      blinkBlue: Math.random() < 0.55,
      rot: angle,
    };
  }

  function spawnShooting(): void {
    // Shoots from upper-left quadrant down toward lower-right, 30°-ish.
    const startX = Math.random() * w * 0.5;
    const startY = Math.random() * h * 0.4;
    const angle = Math.PI * 0.16 + Math.random() * Math.PI * 0.12;
    const speed = 520 + Math.random() * 380;
    shooting = {
      x: startX,
      y: startY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
    };
  }

  function resize(): void {
    w = window.innerWidth;
    h = window.innerHeight;
    canvas!.width = w * dpr;
    canvas!.height = h * dpr;
    canvas!.style.width = w + "px";
    canvas!.style.height = h + "px";
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

    const area = w * h;
    const count = Math.min(280, Math.max(100, Math.round(area / 6000)));
    stars = new Array(count).fill(0).map(() => {
      const z = 0.2 + Math.random() * 0.8;
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        z,
        r: 0.3 + z * 1.5,
        tw: Math.random() * Math.PI * 2,
        speed: 0.6 + Math.random() * 1.8,
        flasher: Math.random() < 0.14,
        warm: Math.random() < 0.18,
        vy: (Math.random() - 0.5) * 0.6,
      };
    });

    const satCount = isMobile ? 3 : 5;
    satellites = new Array(satCount).fill(0).map(() => makeSatellite(false));
  }

  function drawSatellite(sat: Satellite, t: number): void {
    ctx!.save();
    ctx!.translate(sat.x, sat.y);
    ctx!.rotate(sat.rot);

    // Solar panels — two thin lines extending along the travel axis.
    ctx!.strokeStyle = "rgba(170, 195, 225, 0.55)";
    ctx!.lineWidth = 0.9;
    ctx!.beginPath();
    ctx!.moveTo(-sat.panel, 0);
    ctx!.lineTo(-sat.body - 0.4, 0);
    ctx!.moveTo(sat.body + 0.4, 0);
    ctx!.lineTo(sat.panel, 0);
    ctx!.stroke();

    // Tiny perpendicular panel ticks so the wings read as panels, not lines.
    ctx!.strokeStyle = "rgba(140, 170, 205, 0.45)";
    ctx!.lineWidth = 0.7;
    ctx!.beginPath();
    ctx!.moveTo(-sat.panel + 0.2, -1.2); ctx!.lineTo(-sat.panel + 0.2, 1.2);
    ctx!.moveTo(-sat.panel * 0.55, -1.0); ctx!.lineTo(-sat.panel * 0.55, 1.0);
    ctx!.moveTo(sat.panel * 0.55, -1.0); ctx!.lineTo(sat.panel * 0.55, 1.0);
    ctx!.moveTo(sat.panel - 0.2, -1.2); ctx!.lineTo(sat.panel - 0.2, 1.2);
    ctx!.stroke();

    // Body — rounded rectangle reads as the satellite bus.
    ctx!.fillStyle = "rgba(225, 228, 235, 0.92)";
    ctx!.fillRect(-sat.body, -sat.body * 0.75, sat.body * 2, sat.body * 1.5);

    // Blinking beacon (red or blue) — sharper on/off cycle so it reads
    // as a navigation light instead of a soft pulse.
    const blink = Math.sin(t * 0.0055 + sat.blinkPhase);
    if (blink > 0.55) {
      const a = Math.min(1, (blink - 0.55) / 0.25);
      const color = sat.blinkBlue ? "126, 182, 255" : "255, 110, 110";
      ctx!.fillStyle = `rgba(${color}, ${a})`;
      ctx!.shadowColor = `rgba(${color}, ${0.8 * a})`;
      ctx!.shadowBlur = 6;
      ctx!.beginPath();
      ctx!.arc(0, -sat.body * 0.75 - 0.7, 0.85, 0, Math.PI * 2);
      ctx!.fill();
      ctx!.shadowBlur = 0;
    }

    ctx!.restore();
  }

  function frame(t: number): void {
    const dt = last === 0 ? 16 : Math.min(64, t - last);
    last = t;
    const dts = dt / 1000;

    ctx!.clearRect(0, 0, w, h);

    // Stars — visibly drifting parallax + per-star twinkle. ~14% are
    // "flashers" that pulse harder so the field feels alive instead of
    // statically peppered.
    for (const s of stars) {
      // Closer stars move faster — parallax. Plus a gentle, per-star
      // vertical wobble so the drift isn't purely horizontal.
      s.x += 8 * s.z * dts;
      s.y += s.vy * s.z * dts;
      if (s.x > w + 3) s.x = -3;
      if (s.y > h + 3) s.y = -3;
      else if (s.y < -3) s.y = h + 3;

      const phase = t * 0.0022 * s.speed + s.tw;
      let twinkle: number;
      if (s.flasher) {
        // Sharper pulse — sin^4 stays dim most of the time then snaps
        // bright, reading as a real "blink".
        const sn = Math.sin(phase);
        twinkle = 0.35 + 0.65 * (sn * sn * sn * sn);
      } else {
        twinkle = 0.5 + 0.5 * Math.sin(phase);
      }
      ctx!.globalAlpha = (0.2 + twinkle * 0.7) * (0.45 + 0.55 * s.z);
      ctx!.fillStyle = s.warm ? "#FFE6B0" : (s.z > 0.7 ? "#FFFFFF" : "#C8D4E6");
      ctx!.beginPath();
      ctx!.arc(s.x, s.y, s.flasher ? s.r * (0.85 + 0.4 * twinkle) : s.r, 0, Math.PI * 2);
      ctx!.fill();
    }
    ctx!.globalAlpha = 1;

    // Satellites — respawn from an edge once they leave the canvas.
    for (let i = 0; i < satellites.length; i++) {
      const sat = satellites[i];
      sat.x += sat.vx * dts;
      sat.y += sat.vy * dts;
      if (sat.x < -40 || sat.x > w + 40 || sat.y < -40 || sat.y > h + 40) {
        satellites[i] = makeSatellite(true);
        continue;
      }
      drawSatellite(sat, t);
    }

    // Shooting stars — at most one alive at a time, gap of 6-14s between.
    if (!shooting && !reduced && t > nextShootingAt) {
      spawnShooting();
    }
    if (shooting) {
      shooting.x += shooting.vx * dts;
      shooting.y += shooting.vy * dts;
      shooting.life -= dts * 0.85;
      if (shooting.life <= 0 || shooting.x > w + 60 || shooting.y > h + 60) {
        shooting = null;
        nextShootingAt = t + 3000 + Math.random() * 5000;
      } else {
        const tailX = shooting.x - shooting.vx * 0.09;
        const tailY = shooting.y - shooting.vy * 0.09;
        const grad = ctx!.createLinearGradient(shooting.x, shooting.y, tailX, tailY);
        grad.addColorStop(0, `rgba(255, 235, 195, ${shooting.life})`);
        grad.addColorStop(1, "rgba(255, 235, 195, 0)");
        ctx!.strokeStyle = grad;
        ctx!.lineWidth = 1.4;
        ctx!.lineCap = "round";
        ctx!.beginPath();
        ctx!.moveTo(shooting.x, shooting.y);
        ctx!.lineTo(tailX, tailY);
        ctx!.stroke();
      }
    }

    if (!reduced) requestAnimationFrame(frame);
  }

  resize();
  window.addEventListener("resize", resize);
  // Always draw one synchronous frame so background tabs (where rAF is
  // throttled to zero) and reduced-motion users still get a populated
  // starfield instead of a blank canvas. frame() self-arms via rAF when
  // motion is allowed.
  last = performance.now();
  nextShootingAt = last + 3500;
  frame(last);
}

/* -------------------------------------------------------------------------- */
/*  4. Globe parallax (desktop only)                                          */
/* -------------------------------------------------------------------------- */

function initGlobeParallax(): void {
  if (window.matchMedia("(hover: none)").matches) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const stage = document.querySelector(".globe-stage") as HTMLElement | null;
  const globe = document.querySelector(".globe") as HTMLElement | null;
  if (!stage || !globe) return;

  // Keep motion subtle — ±6° on each axis.
  window.addEventListener(
    "mousemove",
    (e) => {
      const nx = (e.clientX / window.innerWidth - 0.5) * 2; // -1..1
      const ny = (e.clientY / window.innerHeight - 0.5) * 2;
      globe.style.transform = `perspective(900px) rotateY(${nx * 6}deg) rotateX(${-ny * 6}deg)`;
    },
    { passive: true }
  );
}

/* -------------------------------------------------------------------------- */
/*  5. Door radial-glow follow                                                */
/* -------------------------------------------------------------------------- */

function initDoorHover(): void {
  if (window.matchMedia("(hover: none)").matches) return;

  const doors = document.querySelectorAll<HTMLElement>(".door");
  doors.forEach((door) => {
    door.addEventListener(
      "mousemove",
      (e) => {
        const rect = door.getBoundingClientRect();
        const mx = ((e.clientX - rect.left) / rect.width) * 100;
        const my = ((e.clientY - rect.top) / rect.height) * 100;
        door.style.setProperty("--mx", mx + "%");
        door.style.setProperty("--my", my + "%");
      },
      { passive: true }
    );
  });
}

/* -------------------------------------------------------------------------- */
/*  Boot                                                                      */
/* -------------------------------------------------------------------------- */

function boot(): void {
  seedHue();
  initSubtitleRotation();
  initStarfield();
  initGlobeParallax();
  initDoorHover();
  // Auth pill goes up early — before the wall — so the "Sign in" affordance
  // is visible the moment the page paints, even if the wall fetch is slow.
  try {
    mountAuthPill();
  } catch (err) {
    console.warn("[landing] auth pill failed to mount:", err);
  }
  initFeedbackWall();
  initHeroProofRefresh();
}

/* -------------------------------------------------------------------------- */
/*  Hero proof refresher                                                      */
/*                                                                            */
/*  The edge function (functions/index.js) fills #hero-proof server-side.    */
/*  This refresher re-fetches the same live feed after load and swaps the    */
/*  chip contents in place, so a service-worker-cached or offline-stale copy */
/*  self-heals to the current signal. Selection + vocabulary MUST stay in    */
/*  sync with functions/index.js (two runtimes, one contract — both files    */
/*  carry this note). On any failure it leaves whatever the edge rendered.   */
/* -------------------------------------------------------------------------- */

interface ProofItem {
  headline?: string;
  suburb_name?: string;
  source?: string;
  url?: string;
  body?: string;
  summary?: string;
  published_at?: string;
  is_city_anchored?: boolean;
  trust_tier?: number | null;
  confidence_pct?: number | null;
  evidence_status?: string | null;
}

const PROOF_FEED_URL =
  "https://hyperlocal-pulse.kuchipudi-kumarchowdary.workers.dev/api/incidents-live/syd";

function proofTierLabel(tier: number | null | undefined): string | null {
  switch (tier) {
    case 1: return "Official source";
    case 2: return "Trusted media";
    case 3: return "Community";
    default: return null;
  }
}

function proofEvidenceLabel(status: string | null | undefined): string | null {
  switch (status) {
    case "verified": return "Verified";
    case "source_reported": return "Source-reported";
    case "inferred": return "Inferred";
    case "disputed": return "Disputed";
    default: return null;
  }
}

function proofTrustLine(item: ProofItem): string | null {
  const parts: string[] = [];
  const evidence = proofEvidenceLabel(item.evidence_status);
  if (evidence) parts.push(evidence);
  if (typeof item.confidence_pct === "number" && Number.isFinite(item.confidence_pct)) {
    parts.push(`Confidence ${Math.round(item.confidence_pct)}%`);
  }
  const tier = proofTierLabel(item.trust_tier);
  if (tier) parts.push(tier);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function proofPick(items: ProofItem[]): ProofItem | null {
  const ms = (it: ProofItem) => {
    const t = Date.parse(it.published_at || "");
    return Number.isFinite(t) ? t : 0;
  };
  // Mirror the edge function (functions/index.js pickSignal): reject
  // future-dated rows so the doorway never headlines "published <future
  // date>", and the freshest-first sort can't prefer one.
  const now = Date.now();
  const SKEW_MS = 60 * 60 * 1000;
  const usable = (items || []).filter(
    (it) =>
      it &&
      typeof it.headline === "string" && it.headline.trim().length > 0 &&
      typeof it.suburb_name === "string" && it.suburb_name.trim().length > 0 &&
      !it.is_city_anchored &&
      proofTrustLine(it) !== null &&
      ms(it) > 0 && ms(it) <= now + SKEW_MS,
  );
  if (usable.length === 0) return null;
  const byFreshness = (a: ProofItem, b: ProofItem) => ms(b) - ms(a);
  const verifiedish = usable.filter((it) => proofEvidenceLabel(it.evidence_status));
  if (verifiedish.length > 0) return verifiedish.sort(byFreshness)[0];
  const official = usable.filter((it) => it.trust_tier === 1);
  if (official.length > 0) return official.sort(byFreshness)[0];
  return usable.sort(byFreshness)[0];
}

function proofSydneyDay(iso: string | undefined): string | null {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return null;
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney", day: "numeric", month: "short",
  }).format(new Date(t));
}

function proofSydneyFull(iso: string | undefined): string | null {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return null;
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney", day: "numeric", month: "short",
    year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
    timeZoneName: "short",
  }).format(new Date(t));
}

function initHeroProofRefresh(): void {
  const slot = document.getElementById("hero-proof");
  if (!slot) return;
  void fetch(PROOF_FEED_URL)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
    .then((data: { items?: ProofItem[]; generated_at?: string }) => {
      const item = proofPick(data.items || []);
      if (!item) return; // leave whatever the edge rendered
      const trust = proofTrustLine(item);
      // Compute and validate EVERYTHING before touching the DOM — a
      // partial apply (new place/trust under the old headline + old date)
      // would render a mixed-provenance chip. Honest dating is the gate:
      // no "as of" line → no claim, leave the edge-rendered state intact.
      const metaParts: string[] = [];
      if (item.source) metaParts.push(String(item.source).trim());
      const published = proofSydneyDay(item.published_at);
      if (published) metaParts.push(`published ${published}`);
      const asOf = proofSydneyFull(data.generated_at);
      if (asOf) metaParts.push(`feed as of ${asOf}`);
      if (metaParts.length === 0) return;

      const setText = (id: string, text: string) => {
        const el = document.getElementById(id);
        if (el) el.textContent = text;
      };
      const a = document.getElementById("hp-headline") as HTMLAnchorElement | null;
      if (a) {
        a.textContent = item.headline!.trim();
        try {
          const u = new URL(item.url || "");
          if (u.protocol === "https:" || u.protocol === "http:") a.href = u.href;
          else a.removeAttribute("href");
        } catch {
          a.removeAttribute("href");
        }
      }
      setText("hp-place", `Sydney — ${item.suburb_name!.trim()}`);
      setText("hp-trust", trust || "");
      setText("hp-why", (item.body || item.summary || "").trim());
      setText("hp-asof", metaParts.join(" · "));
      slot.removeAttribute("hidden");
    })
    .catch(() => {
      /* Feed unreachable — leave the edge-rendered (or hidden) state. */
    });
}

/* -------------------------------------------------------------------------- */
/*  Feedback wall mount                                                       */
/* -------------------------------------------------------------------------- */

// Dynamic import so the wall's code (and the moderation module it pulls in)
// only downloads on pages that actually mount it. Keeps the landing LCP at
// ~6 KB gzipped for users who never scroll to the wall.
function initFeedbackWall(): void {
  const host = document.getElementById("feedback-wall");
  if (!host) return;
  void import("./feedback-wall").then((m) => m.mountFeedbackWall(host as HTMLElement))
    .catch((err) => {
      // Fail silently — the wall is a bonus feature and must never break the
      // landing page. Log to console for diagnostics only.
      console.warn("[landing] feedback wall failed to mount:", err);
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
